import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecommendation } from '../context/RecommendationContext';
import { getSampleDecisionResult, runDecisionEngine } from '../api/decisionEngine';
import type { DecisionEngineResult } from '../api/decisionEngine';
import type { SystemRecInput } from '@core/types';
import { normalizeSystemRecOutput } from '@core/normalizeOutput';
import { DebugBundleButton } from '../components/DebugBundleButton';
import { IP_NOTICE_MINIMAL } from '../legal/ipNotice';
import { buildDecisionPack } from '@core/presentationPrint';
import { buildCompactPrint } from '@core/compactPrint';
import { loadStorySnapshot, getSnapshotAge } from '../utils/storySnapshot';
import { getOrCreateDeepLink } from '../utils/deepLink';
import { retrieveDocument } from '../utils/documentPersistence';
import { EmailReportForm } from '../components/EmailReportForm';
import { QRCode } from '../components/print/QRCode';
import {
  CoverPage,
  OptionPage,
  SummaryPage,
  AppendixPage,
  PrintSelectionUI,
  SavingsPageWrapper,
  HowWeWorkedItOutWrapper,
  CompactPrintView,
  AsymmetricSwotPage,
  TechnicalAppendixPage,
  TechnicalDisclaimerPage,
} from '../components/print';
import type { PrintSelections } from '../components/print';
import type { SavingsPageProps, HowWeWorkedItOutProps } from '@system-rec/decision-ui';

type PrintMode = 'full' | 'compact';
type ExportBundle = 'all' | 'summary' | 'technical' | 'appendix' | 'option';

// Styles for export bundle radio buttons
const RADIO_BUTTON_STYLES = {
  base: {
    padding: '0.75rem',
    border: '2px solid',
    borderRadius: '6px',
    cursor: 'pointer' as const,
  },
  selected: {
    background: '#e0f2fe',
    borderColor: '#0284c7',
  },
  unselected: {
    background: 'white',
    borderColor: '#e2e8f0',
  },
};

/**
 * DECISION PACK v2 — Export / Print Page
 *
 * This is the ONLY thing the customer leaves with.
 * Everything else is scaffolding.
 *
 * Global print rules (frozen):
 *  ✅ Print-first (screen just previews)
 *  ✅ Each option = exactly one A4 page
 *  ✅ £/year only
 *  ❌ No replacements
 *  ❌ No timelines
 *  ❌ No carbon
 *  ❌ No "doing nothing"
 *  ❌ No technical tables
 *
 * Output bundles:
 *  📄 Summary PDF (compact, customer-friendly)
 *  📄 Technical PDF (full report with all details)
 *  📄 Individual Reference PDFs (each standalone)
 *
 * Output order: Cover → Option pages → Summary → Appendices
 */

function ExportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { recommendationResult, collectedData, convertToSystemRecInput, setRecommendationResult } = useRecommendation();
  const [result, setResult] = useState<DecisionEngineResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snapshotWarning, setSnapshotWarning] = useState<string | null>(null);
  const [hasNoSnapshot, setHasNoSnapshot] = useState(false);
  const [printMode, setPrintMode] = useState<PrintMode>('compact'); // Default to compact
  const [exportBundle, setExportBundle] = useState<ExportBundle>('all'); // Which PDF to generate
  const [selectedAppendixId, setSelectedAppendixId] = useState<string | null>(null); // For individual appendix export
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null); // For individual option export
  const [deepLink, setDeepLink] = useState<{ url: string; documentId: string } | null>(null);
  const [deepLinkError, setDeepLinkError] = useState(false);
  const [deepLinkErrorDetails, setDeepLinkErrorDetails] = useState<any>(null);
  const [exportState, setExportState] = useState<'idle' | 'working' | 'error'>('idle');
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  // Resolve the engine input via fallback chain:
  // 1. inputEcho (always present when engine ran in this session)
  // 2. normalizedInput (stored on DecisionEngineResult)
  // 3. Reconstruct from collectedData (last resort)
  const effectiveInput = useMemo((): SystemRecInput | null => {
    if (result?.output?.inputEcho) return result.output.inputEcho;
    if ((result as any)?.normalizedInput) return (result as any).normalizedInput;
    if (collectedData && convertToSystemRecInput) {
      try {
        return convertToSystemRecInput(collectedData);
      } catch {
        return null;
      }
    }
    return null;
  }, [result, collectedData, convertToSystemRecInput]);

  // Check if required data exists for export
  const hasRequiredData = useMemo(() => {
    return !!(result?.output?.primary && effectiveInput);
  }, [result, effectiveInput]);
  
  // Compute print button disabled state
  const isPrintDisabled = useMemo(() => {
    return (
      (exportBundle === 'appendix' && !selectedAppendixId) ||
      (exportBundle === 'option' && !selectedOptionId) ||
      exportState === 'error'
    );
  }, [exportBundle, selectedAppendixId, selectedOptionId, exportState]);

  useEffect(() => {
    // HARD ASSERTION: Validate that we have real recommendation data
    console.log('🔍 ExportPage: Validating recommendation data...');
    
    // Check for story snapshot
    const snapshot = loadStorySnapshot();
    
    // If we have recommendationResult from context (loaded from sessionStorage),
    // use that even if there's no snapshot file
    if (recommendationResult) {
      // VALIDATE: Ensure this is not empty/default data
      const hasValidPrimary = recommendationResult.output?.primary;
      const hasValidInput = recommendationResult.output?.inputEcho;
      
      if (!hasValidPrimary) {
        console.error('❌ ExportPage: recommendationResult has no primary recommendation');
        setHasNoSnapshot(true);
        setSnapshotWarning('Invalid recommendation data: No primary option found. Please complete the recommendation flow again.');
        setLoading(false);
        return;
      }
      
      if (!hasValidInput) {
        console.warn('⚠️ ExportPage: recommendationResult has no input echo - this may cause issues');
      }
      
      console.log('✅ ExportPage: Valid recommendation data found', {
        primary: hasValidPrimary ? recommendationResult.output.primary.title : 'none',
        alternatives: recommendationResult.output?.alternatives?.length || 0,
      });
      
      // Convert RecommendationResult to DecisionEngineResult format
      // Note: recommendationResult may not have normalizedOutput, so we create it if needed
      const resultWithNormalized: DecisionEngineResult = {
        ...recommendationResult,
        normalizedOutput: (recommendationResult as any).normalizedOutput || normalizeSystemRecOutput(
          recommendationResult.output,
          recommendationResult.normalizedInput || {} as any
        ),
        normalizedInput: recommendationResult.normalizedInput || {} as any,
      };
      
      setResult(resultWithNormalized);
      setLoading(false);
      setHasNoSnapshot(false);
      return;
    }
    
    if (!snapshot) {
      console.error('❌ ExportPage: No snapshot and no recommendationResult');
      setHasNoSnapshot(true);
      setSnapshotWarning('No story data found. Please complete the recommendation flow first.');
      setLoading(false);
      return;
    }
    
    setHasNoSnapshot(false);
    
    // Check if snapshot is stale (older than 24 hours)
    const age = getSnapshotAge();
    if (age !== null && age > 24 * 60 * 60 * 1000) {
      const hours = Math.floor(age / (1000 * 60 * 60));
      setSnapshotWarning(`Story data is ${hours} hours old. Consider re-running the recommendation for up-to-date results.`);
    }
    
    if (recommendationResult) {
      setResult(recommendationResult);
      setLoading(false);
    } else {
      // Fall back to sample data only in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ ExportPage: Using sample data - no recommendation result available');
        getSampleDecisionResult()
          .then(setResult)
          .catch((err) => setError(err.message))
          .finally(() => setLoading(false));
      } else {
        console.error('❌ ExportPage: No recommendation result in production');
        setError('No recommendation result available. Please complete the story flow first.');
        setLoading(false);
      }
    }
  }, [recommendationResult]);

  // Build Decision Pack from engine output - wrapped in try-catch
  const pack = useMemo(() => {
    if (!result || !effectiveInput) return null;
    try {
      return buildDecisionPack(result.output, effectiveInput);
    } catch (e) {
      console.error('Failed to build decision pack:', e);
      setError('Failed to build export document. Please try again or contact support.');
      return null;
    }
  }, [result, effectiveInput]);

  const packWithDocRef = useMemo(() => {
    if (!pack) return null;
    const docRef = collectedData?.documentReference?.trim() || deepLink?.documentId?.toUpperCase();
    const customerRef = collectedData?.customerReference?.trim();
    if (!docRef && !customerRef) return pack;
    const refBits = [
      docRef ? `Doc ref: ${docRef}` : null,
      customerRef ? `Customer ref: ${customerRef}` : null,
    ].filter(Boolean).join(' · ');
    return {
      ...pack,
      cover: {
        ...pack.cover,
        generatedDate: refBits ? `${pack.cover.generatedDate} · ${refBits}` : pack.cover.generatedDate,
      },
    };
  }, [pack, deepLink?.documentId, collectedData?.documentReference, collectedData?.customerReference]);

  // Build Compact Print from engine output - wrapped in try-catch
  const compactPack = useMemo(() => {
    if (!result || !effectiveInput) return null;
    try {
      return buildCompactPrint(result.output, effectiveInput);
    } catch (e) {
      // Compact print requires both primary and secondary recommendations
      console.warn('Compact print not available:', e);
      return null;
    }
  }, [result, effectiveInput]);

  // Update compactPack with actual deepLink when available
  const compactPackWithDeepLink = useMemo(() => {
    if (!compactPack || !deepLink) return compactPack;
    
    // Update the QR code with the real deep link
    return {
      ...compactPack,
      documentId: deepLink.documentId,
      nextSteps: {
        ...compactPack.nextSteps,
        qrCode: {
          baseUrl: window.location.origin,
          documentId: deepLink.documentId,
          fullUrl: deepLink.url,
          sizeHint: 150,
        },
      },
    };
  }, [compactPack, deepLink]);

  // Generate deep link for QR code - done async after result is available
  // CRITICAL: PDF export MUST be blocked if this fails
  useEffect(() => {
    if (result && hasRequiredData) {
      setExportState('working');
      getOrCreateDeepLink()
        .then(link => {
          if (!link) {
            console.error('Failed to generate deep link: no link returned');
            setDeepLink(null);
            setDeepLinkError(true);
            setDeepLinkErrorDetails({
              error: 'No link returned',
              endpoint: '/api/story/persist (via getOrCreateDeepLink)'
            });
            setExportState('error');
            return;
          }
          setDeepLink(link);
          setDeepLinkError(false);
          setDeepLinkErrorDetails(null);
          setExportState('idle');
        })
        .catch(err => {
          console.error('CRITICAL: Failed to persist document for export:', err);
          setDeepLink(null);
          setDeepLinkError(true);
          setDeepLinkErrorDetails({
            message: err.message || 'Unknown error',
            code: err.code || 'UNKNOWN_ERROR',
            status: err.status || 500,
            endpoint: err.endpoint || '/api/story/persist',
            details: err.details || null
          });
          setExportState('error');
          // Keep error state - do NOT reset to idle
          // User must reload the page or navigate back to try again
          // This ensures invalid documents are never exported
        });
    }
  }, [result, hasRequiredData]);

  // Build SavingsPage props from year1 data (if available)
  const savingsPageProps = useMemo((): SavingsPageProps | null => {
    if (!result?.output.year1) return null;
    
    const year1 = result.output.year1;
    const baseline = year1.baseline;
    
    // Use the primary option for savings comparison
    const primaryOption = year1.options.find(opt => opt.optionId === result.output.primary.id);
    if (!primaryOption) return null;

    return {
      baseline: {
        costGbp: baseline.costGbp,
        energyKwh: baseline.fuelKwh.gas || baseline.fuelKwh.electricity || 0,
        carbonKg: baseline.carbonKg,
      },
      option: {
        label: primaryOption.label,
        costGbp: primaryOption.impact.costGbp,
        energyKwh: primaryOption.impact.fuelKwh.gas || primaryOption.impact.fuelKwh.electricity || 0,
        carbonKg: primaryOption.impact.carbonKg,
      },
    };
  }, [result]);

  // Build HowWeWorkedItOut props from year1 data (if available)
  const howWeWorkedItOutProps = useMemo((): HowWeWorkedItOutProps | null => {
    if (!result?.output.year1) return null;
    
    const year1 = result.output.year1;
    const baseline = year1.baseline;
    
    // Use the primary option for detailed comparison
    const primaryOption = year1.options.find(opt => opt.optionId === result.output.primary.id);
    if (!primaryOption) return null;

    // Get tariff information from the input echo or use defaults
    const gasTariff = 5.93;  // Default p/kWh
    const electricityTariff = 27.69;  // Default p/kWh
    const carbonFactorGas = 0.210;  // kgCO2/kWh
    const carbonFactorElectricity = 0.233;  // kgCO2/kWh

    return {
      recommendationState: result.output.state,
      baseline: {
        spaceHeatKwh: baseline.spaceHeatKwh,
        dhwKwh: baseline.dhwKwh,
        totalHeatKwh: baseline.totalHeatKwh,
        performance: baseline.performance,
        fuelKwh: baseline.fuelKwh,
        costGbp: baseline.costGbp,
        carbonKg: baseline.carbonKg,
      },
      option: {
        label: primaryOption.label,
        detail: {
          spaceHeatKwh: primaryOption.impact.spaceHeatKwh,
          dhwKwh: primaryOption.impact.dhwKwh,
          totalHeatKwh: primaryOption.impact.totalHeatKwh,
          performance: primaryOption.impact.performance,
          fuelKwh: primaryOption.impact.fuelKwh,
          costGbp: primaryOption.impact.costGbp,
          carbonKg: primaryOption.impact.carbonKg,
        },
      },
      assumptions: {
        gasTariffPPerKwh: gasTariff,
        electricityTariffPPerKwh: electricityTariff,
        carbonFactorGas: carbonFactorGas,
        carbonFactorElectricity: carbonFactorElectricity,
        heatLossSource: result.output.inputEcho?.heatLoss 
          ? `User-provided heat loss calculation: ${result.output.inputEcho.heatLoss} kW`
          : 'Heat loss estimated from property characteristics',
        performanceSource: baseline.performance.source === 'sedbuk'
          ? `SEDBUK database: ${baseline.performance.label}${
              result.output.inputEcho?.currentSystemEfficiency?.matchedBoiler
                ? ` (${result.output.inputEcho.currentSystemEfficiency.matchedBoiler.make} ${result.output.inputEcho.currentSystemEfficiency.matchedBoiler.model})`
                : result.output.inputEcho?.currentSystemEfficiency?.sedbukBand
                ? ` (Band ${result.output.inputEcho.currentSystemEfficiency.sedbukBand})`
                : ''
            }`
          : baseline.performance.source === 'epc'
          ? `EPC certificate data: ${baseline.performance.label}`
          : `Typical values for system type and age: ${baseline.performance.label}`,
      },
      confidence: {
        level: baseline.performance.confidence,
        reason: baseline.performance.confidence === 'high'
          ? 'Based on detailed property data and verified system specifications'
          : baseline.performance.confidence === 'medium'
          ? 'Based on typical values for homes of this type and age'
          : 'Limited data available - estimates based on broad assumptions',
      },
    };
  }, [result]);

  // Print selections state
  const [selections, setSelections] = useState<PrintSelections | null>(null);

  // Initialize selections once pack is available
  useEffect(() => {
    if (pack && !selections) {
      setSelections({
        includeCover: true,
        includeSummary: true,
        selectedOptionIds: packWithDocRef.optionPages.map((p) => p.optionId),
        selectedAppendixIds: [], // Off by default
      });
    }
  }, [pack, selections]);

  const handlePrint = () => {
    try {
      setExportState('working');
      window.print();
      setExportState('idle');
    } catch (e) {
      console.error('Print failed:', e);
      setExportState('error');
      alert('Failed to open print dialog. Please try again.');
      setTimeout(() => setExportState('idle'), 3000);
    }
  };

  const handleRebuildSummary = useCallback(async () => {
    console.log('🔄 Rebuilding summary...');
    setLoading(true);
    setError(null);
    
    try {
      // CANONICAL PAYLOAD SOURCE LOGIC:
      // 1. If docId exists in URL: load from persisted document (shared link scenario)
      // 2. Else: use live UI state from collectedData (normal flow scenario)
      
      const docIdParam = searchParams.get('docId');
      
      if (docIdParam) {
        // Scenario 1: Rebuild from persisted document (shared link)
        console.log('📄 Rebuilding from persisted document:', docIdParam);
        
        const snapshot = await retrieveDocument(docIdParam);
        
        if (!snapshot?.engineOutput) {
          throw new Error('Retrieved document has no engine output');
        }
        
        // Rebuild using the persisted snapshot's input
        const result = await runDecisionEngine(snapshot.engineOutput.input);
        
        console.log('✅ Summary rebuilt successfully from document');
        setRecommendationResult(result);
        setResult(result);
        setLoading(false);
      } else if (collectedData && convertToSystemRecInput) {
        // Scenario 2: Rebuild from live UI state (normal flow)
        console.log('📊 Rebuilding from live UI state');
        
        const input = convertToSystemRecInput(collectedData);
        const result = await runDecisionEngine(input);
        
        console.log('✅ Summary rebuilt successfully from live state');
        setRecommendationResult(result);
        setResult(result);
        setLoading(false);
      } else {
        // No data available at all
        console.log('⚠️ No data available for rebuild, navigating to story flow');
        navigate('/hub');
      }
    } catch (err) {
      console.error('❌ Failed to rebuild summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to rebuild summary');
      setLoading(false);
    }
  }, [collectedData, convertToSystemRecInput, setRecommendationResult, navigate, searchParams]);

  if (loading) {
    return (
      <div className="page export-page">
        <div className="dp-print-selection no-print">
          <div className="dp-selection-controls">
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              &larr; Back
            </button>
          </div>
        </div>
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center',
          fontSize: '1.125rem',
          color: '#64748b'
        }}>
          <p>⏳ Loading decision pack...</p>
        </div>
      </div>
    );
  }

  // Show error if no data or failed to load
  if (error || !result || !hasRequiredData) {
    return (
      <div className="page export-page">
        <div className="dp-print-selection no-print">
          <div className="dp-selection-controls">
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              &larr; Back
            </button>
            <button onClick={handleRebuildSummary} className="btn btn-primary">
              🔄 Rebuild Summary
            </button>
          </div>
        </div>
        <div style={{ 
          padding: '2rem', 
          background: '#fef2f2', 
          border: '2px solid #fca5a5', 
          borderRadius: '8px',
          margin: '2rem'
        }}>
          <h2 style={{ color: '#991b1b', marginBottom: '1rem' }}>
            ⚠️ {!hasRequiredData ? 'Missing Export Data' : 'No Recommendation Data Available'}
          </h2>
          <p style={{ color: '#7f1d1d', marginBottom: '1rem' }}>
            {!hasRequiredData 
              ? 'We need to rebuild your summary before exporting. Some required data is missing from the recommendation.'
              : error || 'Please complete the recommendation flow before viewing the export.'
            }
          </p>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button 
              onClick={handleRebuildSummary} 
              className="btn btn-primary"
              style={{ 
                padding: '0.75rem 1.5rem',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Rebuild Summary →
            </button>
            <button 
              onClick={() => navigate(-1)} 
              className="btn btn-secondary"
              style={{ 
                padding: '0.75rem 1.5rem',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

    if (!packWithDocRef || !selections) {
      return null;
    }

  // Show snapshot warning if present (non-blocking, not for missing data)
  const showSnapshotWarning = snapshotWarning && !hasNoSnapshot;

  // Filter by selections
  const visibleOptions = packWithDocRef.optionPages.filter((p) =>
    selections.selectedOptionIds.includes(p.optionId)
  );
  const visibleAppendices = packWithDocRef.appendices.filter((a) =>
    selections.selectedAppendixIds.includes(a.id)
  );

  // Rebuild summary rows to match visible options only
  const visibleSummary = {
    ...packWithDocRef.summary,
    rows: packWithDocRef.summary.rows.filter((_, i) =>
      selections.selectedOptionIds.includes(packWithDocRef.optionPages[i]?.optionId)
    ),
  };

  return (
    <div className="page export-page dp-decision-pack">
      {/* Screen-only: snapshot warning */}
      {showSnapshotWarning && (
        <div className="no-print" style={{ 
          padding: '1rem', 
          background: '#fef3c7', 
          border: '1px solid #fbbf24', 
          borderRadius: '8px',
          margin: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.25rem' }}>⚠️</span>
          <span style={{ color: '#92400e' }}>{snapshotWarning}</span>
        </div>
      )}

      {/* Screen-only: export state indicator */}
      {exportState === 'working' && (
        <div className="no-print" style={{ 
          padding: '0.75rem', 
          background: '#e0f2fe', 
          border: '1px solid #7dd3fc', 
          borderRadius: '8px',
          margin: '1rem',
          textAlign: 'center',
          color: '#0369a1'
        }}>
          ⏳ Preparing your report...
        </div>
      )}
      {exportState === 'error' && deepLinkError && (
        <div className="no-print" style={{ 
          padding: '1rem', 
          background: '#fef2f2', 
          border: '2px solid #ef4444', 
          borderRadius: '8px',
          margin: '1rem'
        }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem', textAlign: 'center' }}>⚠️</div>
          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center', color: '#991b1b' }}>
            Cannot Generate Export
          </div>
          <div style={{ fontSize: '0.9rem', textAlign: 'center', color: '#991b1b' }}>
            Failed to save your report to the database. The QR code and share link cannot be generated.
          </div>
          <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#7f1d1d', textAlign: 'center' }}>
            Please try again or contact support if the problem persists.
          </div>
          
          {/* Show details expander */}
          <div style={{ marginTop: '1rem', borderTop: '1px solid #fca5a5', paddingTop: '0.75rem' }}>
            <button
              onClick={() => setShowErrorDetails(!showErrorDetails)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#991b1b',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0',
                width: '100%',
                justifyContent: 'center'
              }}
            >
              <span style={{ transform: showErrorDetails ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                ▶
              </span>
              Show details
            </button>
            
            {showErrorDetails && deepLinkErrorDetails && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: '#fff',
                borderRadius: '4px',
                border: '1px solid #fca5a5',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                color: '#7f1d1d',
                maxHeight: '300px',
                overflowY: 'auto',
                textAlign: 'left'
              }}>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Endpoint:</strong> {deepLinkErrorDetails.endpoint || 'Unknown'}
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>HTTP Status:</strong> {deepLinkErrorDetails.status || 'Unknown'}
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Error Code:</strong> {deepLinkErrorDetails.code || 'UNKNOWN_ERROR'}
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Message:</strong> {deepLinkErrorDetails.message || 'No message available'}
                </div>
                {deepLinkErrorDetails.details && (
                  <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #fca5a5' }}>
                    <strong>Additional Details:</strong>
                    <pre style={{ 
                      marginTop: '0.25rem', 
                      padding: '0.5rem', 
                      background: '#fef2f2', 
                      borderRadius: '2px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {JSON.stringify(deepLinkErrorDetails.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Screen-only: print mode toggle */}
      <div className="dp-print-selection no-print">
        <div className="dp-mode-toggle">
          <button
            className={`dp-mode-btn ${printMode === 'compact' ? 'active' : ''}`}
            onClick={() => setPrintMode('compact')}
          >
            📄 Compact (4 pages + QR)
          </button>
          <button
            className={`dp-mode-btn ${printMode === 'full' ? 'active' : ''}`}
            onClick={() => setPrintMode('full')}
          >
            📑 Full Report ({packWithDocRef.optionPages.length + 3}+ pages)
          </button>
        </div>
        {!compactPackWithDeepLink && printMode === 'compact' && (
          <div style={{ padding: '0.75rem', background: '#fef3c7', borderRadius: '6px', marginTop: '0.5rem' }}>
            <span style={{ color: '#92400e' }}>
              ⚠️ Compact print requires two recommendations. Showing full report instead.
            </span>
          </div>
        )}
      </div>

      {/* Screen-only: Export Bundle Selector */}
      {(printMode === 'full' || !compactPackWithDeepLink) && (
        <div className="dp-print-selection no-print">
          <div className="dp-selection-controls">
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              &larr; Back
            </button>
            <button onClick={() => navigate('/story/brief')} className="btn btn-secondary">
              Continue to presentation
            </button>
            <button 
              onClick={handlePrint} 
              className="btn btn-primary"
              disabled={isPrintDisabled}
              style={{
                opacity: isPrintDisabled ? 0.5 : 1,
                cursor: isPrintDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              🖨️ Export Selected Bundle
            </button>
          </div>

          {/* Export Bundle Selection */}
          <div className="dp-selection-group" style={{ marginBottom: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '0.75rem', fontSize: '16px', fontWeight: '600' }}>📄 Export Bundle</h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '1rem' }}>
              Choose which PDF to generate. Each bundle is standalone with its own title page.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label 
                className="dp-checkbox-label" 
                style={{
                  ...RADIO_BUTTON_STYLES.base,
                  ...(exportBundle === 'summary' ? RADIO_BUTTON_STYLES.selected : RADIO_BUTTON_STYLES.unselected)
                }}
              >
                <input
                  type="radio"
                  name="exportBundle"
                  checked={exportBundle === 'summary'}
                  onChange={() => setExportBundle('summary')}
                  style={{ marginRight: '0.5rem' }}
                />
                <strong>Summary PDF</strong> — Compact customer-friendly version (Cover + Options + Summary + QR)
              </label>

              <label 
                className="dp-checkbox-label" 
                style={{
                  ...RADIO_BUTTON_STYLES.base,
                  ...(exportBundle === 'technical' ? RADIO_BUTTON_STYLES.selected : RADIO_BUTTON_STYLES.unselected)
                }}
              >
                <input
                  type="radio"
                  name="exportBundle"
                  checked={exportBundle === 'technical'}
                  onChange={() => setExportBundle('technical')}
                  style={{ marginRight: '0.5rem' }}
                />
                <strong>Technical PDF</strong> — Full report with savings, technical details, and all options
              </label>

              <label 
                className="dp-checkbox-label" 
                style={{
                  ...RADIO_BUTTON_STYLES.base,
                  ...(exportBundle === 'appendix' ? RADIO_BUTTON_STYLES.selected : RADIO_BUTTON_STYLES.unselected)
                }}
              >
                <input
                  type="radio"
                  name="exportBundle"
                  checked={exportBundle === 'appendix'}
                  onChange={() => setExportBundle('appendix')}
                  style={{ marginRight: '0.5rem' }}
                />
                <strong>Reference topic PDF</strong> — Export one technical explainer as a standalone PDF
              </label>

              {exportBundle === 'appendix' && (
                <div style={{ marginLeft: '2rem', marginTop: '0.5rem' }}>
                  <select 
                    value={selectedAppendixId || ''}
                    onChange={(e) => setSelectedAppendixId(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem', 
                      borderRadius: '4px', 
                      border: '1px solid #cbd5e1',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select a topic...</option>
                    {packWithDocRef.appendices.map((app) => (
                      <option key={app.id} value={app.id}>
                        {app.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <label 
                className="dp-checkbox-label" 
                style={{
                  ...RADIO_BUTTON_STYLES.base,
                  ...(exportBundle === 'option' ? RADIO_BUTTON_STYLES.selected : RADIO_BUTTON_STYLES.unselected)
                }}
              >
                <input
                  type="radio"
                  name="exportBundle"
                  checked={exportBundle === 'option'}
                  onChange={() => setExportBundle('option')}
                  style={{ marginRight: '0.5rem' }}
                />
                <strong>Individual option PDF</strong> — Export one system option as its own PDF
              </label>

              {exportBundle === 'option' && (
                <div style={{ marginLeft: '2rem', marginTop: '0.5rem' }}>
                  <select 
                    value={selectedOptionId || ''}
                    onChange={(e) => setSelectedOptionId(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem', 
                      borderRadius: '4px', 
                      border: '1px solid #cbd5e1',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select an option...</option>
                    {packWithDocRef.optionPages.map((opt) => (
                      <option key={opt.optionId} value={opt.optionId}>
                        {opt.header.systemName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <label 
                className="dp-checkbox-label" 
                style={{
                  ...RADIO_BUTTON_STYLES.base,
                  ...(exportBundle === 'all' ? RADIO_BUTTON_STYLES.selected : RADIO_BUTTON_STYLES.unselected)
                }}
              >
                <input
                  type="radio"
                  name="exportBundle"
                  checked={exportBundle === 'all'}
                  onChange={() => setExportBundle('all')}
                  style={{ marginRight: '0.5rem' }}
                />
                <strong>Complete Bundle</strong> — Everything including selected reference topics (legacy mode)
              </label>
            </div>

            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '6px', fontSize: '13px' }}>
              💡 <strong>Tip:</strong> Generate Summary PDF for customers, Technical PDF for installers, and individual reference topics for detailed explanations.
            </div>
          </div>
        </div>
      )}

      {/* Screen-only: print selection UI (only for legacy 'all' mode) */}
      {(printMode === 'full' || !compactPackWithDeepLink) && exportBundle === 'all' && (
        <PrintSelectionUI
          options={packWithDocRef.optionPages}
          appendices={packWithDocRef.appendices}
          selections={selections}
          onChange={setSelections}
          onPrint={handlePrint}
          onBack={() => navigate(-1)}
        />
      )}
      {(printMode === 'full' || !compactPackWithDeepLink) && exportBundle === 'all' && (
        <div className="dp-print-selection no-print">
          <div className="dp-selection-controls">
            <button onClick={() => navigate('/story/brief')} className="btn btn-secondary">
              Continue to presentation
            </button>
          </div>
        </div>
      )}
      {deepLink?.url && (printMode === 'full' || !compactPackWithDeepLink) && exportBundle === 'all' && (
        <div className="dp-print-selection no-print">
          <div className="dp-selection-controls">
            <a
              className="btn btn-secondary"
              href={deepLink.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Send link to portal
            </a>
          </div>
        </div>
      )}

      {/* Screen-only: compact mode print button */}
      {printMode === 'compact' && compactPackWithDeepLink && (
        <div className="dp-print-selection no-print">
          <div className="dp-selection-controls">
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              &larr; Back
            </button>
            <button 
              onClick={handlePrint} 
              className="btn btn-primary"
              disabled={exportState === 'error'}
              style={{
                opacity: exportState === 'error' ? 0.5 : 1,
                cursor: exportState === 'error' ? 'not-allowed' : 'pointer'
              }}
            >
              🖨️ Print / Save as PDF
            </button>
            {deepLink?.url && (
              <a
                className="btn btn-secondary"
                href={deepLink.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Send link to portal
              </a>
            )}
          </div>
          {/* Email report option */}
          <div style={{ marginTop: '1rem', maxWidth: '400px' }}>
            <EmailReportForm
              isPrimary={false}
              showCompactOption={false}
              labelText="Email this report"
              subText="Send a copy to yourself or share with others."
            />
          </div>
        </div>
      )}

      {/* Screen-only: debug tools */}
      <div className="no-print" style={{ padding: '0.5rem 1rem' }}>
        <DebugBundleButton />
      </div>

      {/* ─── Printed pages ─── */}

      {/* COMPACT MODE: 4-page decision document with QR */}
      {printMode === 'compact' && compactPackWithDeepLink && (
        <CompactPrintView data={compactPackWithDeepLink} />
      )}

      {/* FULL MODE: Render based on selected export bundle */}
      {(printMode === 'full' || !compactPackWithDeepLink) && (
        <>
          {/* SUMMARY BUNDLE: Cover + Options + Summary + QR */}
          {exportBundle === 'summary' && compactPackWithDeepLink && (
            <CompactPrintView data={compactPackWithDeepLink} />
          )}

          {/* TECHNICAL BUNDLE: Full report with savings and technical details */}
          {exportBundle === 'technical' && (
            <>
              {/* Title page for Technical PDF */}
              <div className="dp-page" style={{ 
                padding: '2rem', 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center',
                minHeight: '100vh'
              }}>
                <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '1rem', color: '#0f172a' }}>
                  Technical Report
                </h1>
                <p style={{ fontSize: '18px', color: '#64748b', marginBottom: '2rem' }}>
                  Detailed heating system recommendation and analysis
                </p>
                <div style={{ fontSize: '14px', color: '#64748b', marginTop: 'auto' }}>
                  <p><strong>Atlas PHM</strong></p>
                  <p>Document ID: {deepLink ? deepLink.documentId.toUpperCase() : 'N/A'}</p>
                  <p>Generated: {new Date().toLocaleDateString('en-GB', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</p>
                </div>
              </div>

              {selections.includeCover && <CoverPage data={packWithDocRef.cover} />}
              {savingsPageProps && <SavingsPageWrapper {...savingsPageProps} />}
              {howWeWorkedItOutProps && <HowWeWorkedItOutWrapper {...howWeWorkedItOutProps} />}
              {packWithDocRef.optionPages.map((optData) => (
                <OptionPage key={optData.optionId} data={optData} />
              ))}
              {selections.includeSummary && visibleSummary.rows.length > 0 && (
                <SummaryPage data={visibleSummary} />
              )}

              {/* Portrait SWOT Stack page (if available) */}
              {packWithDocRef.swotPage && (
                <AsymmetricSwotPage data={packWithDocRef.swotPage} />
              )}

              {/* Technical Appendix with diagrams (if available) */}
              {packWithDocRef.technicalAppendix && (
                <TechnicalAppendixPage data={packWithDocRef.technicalAppendix} />
              )}

              {/* Technical Disclaimer page (if available) */}
              {packWithDocRef.technicalDisclaimer && (
                <TechnicalDisclaimerPage data={packWithDocRef.technicalDisclaimer} />
              )}

              {/* Footer for Technical PDF */}
              <div className="dp-print-footer">
                {deepLink && (
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '12px',
                    paddingBottom: '12px',
                    borderBottom: '1px solid #e2e8f0',
                  }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '8px',
                      background: 'white',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                    }}>
                      <QRCode url={deepLink.url} size={100} />
                    </div>
                    <p style={{ fontSize: '10px', color: '#64748b', marginTop: '4px', marginBottom: '4px' }}>
                      Scan to view your interactive summary
                    </p>
                    <a
                      href={deepLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '9px',
                        color: '#2563eb',
                        textDecoration: 'underline',
                        display: 'inline-block',
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: 10
                      }}
                    >
                      Open interactive report
                    </a>
                  </div>
                )}
                <p>
                  {IP_NOTICE_MINIMAL} |
                  Generated: {new Date().toLocaleDateString('en-GB')}
                  {deepLink ? ` | Document ID: ${deepLink.documentId.toUpperCase()}` : ''}
                  {' | Report layout: V2'}
                </p>
              </div>
            </>
          )}

          {/* INDIVIDUAL REFERENCE TOPIC BUNDLE */}
          {exportBundle === 'appendix' && selectedAppendixId && (
            <>
              {(() => {
                const appendix = packWithDocRef.appendices.find(a => a.id === selectedAppendixId);
                if (!appendix) return null;
                const appendixIndex = packWithDocRef.appendices.findIndex(a => a.id === selectedAppendixId);

                return (
                  <>
                    {/* Title page for Reference Topic PDF */}
                    <div className="dp-page" style={{ 
                      padding: '2rem', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'center',
                      minHeight: '100vh'
                    }}>
                      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '1rem', color: '#0f172a' }}>
                        {appendix.title}
                      </h1>
                      <p style={{ fontSize: '18px', color: '#64748b', marginBottom: '2rem' }}>
                        Technical reference topic
                      </p>
                      <div style={{ fontSize: '14px', color: '#64748b', marginTop: 'auto' }}>
                        <p><strong>Atlas PHM</strong> • System Recommendation Reference</p>
                        <p>Document ID: {deepLink ? deepLink.documentId.toUpperCase() : 'N/A'}</p>
                        <p>Generated: {new Date().toLocaleDateString('en-GB', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}</p>
                      </div>
                    </div>

                    {/* Reference content */}
                    <AppendixPage key={appendix.id} data={appendix} index={appendixIndex} />

                    {/* Footer for Reference Topic PDF */}
                    <div className="dp-print-footer">
                      <p>
                        {IP_NOTICE_MINIMAL} |
                        Generated: {new Date().toLocaleDateString('en-GB')}
                        {deepLink ? ` | Document ID: ${deepLink.documentId.toUpperCase()}` : ''}
                      </p>
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {/* INDIVIDUAL OPTION BUNDLE */}
          {exportBundle === 'option' && selectedOptionId && (
            <>
              {(() => {
                const option = packWithDocRef.optionPages.find(opt => opt.optionId === selectedOptionId);
                if (!option) return null;
                return (
                  <>
                    <div className="dp-page" style={{ 
                      padding: '2rem', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'center',
                      minHeight: '100vh'
                    }}>
                      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '1rem', color: '#0f172a' }}>
                        {option.header.systemName}
                      </h1>
                      <p style={{ fontSize: '18px', color: '#64748b', marginBottom: '2rem' }}>
                        Individual system option
                      </p>
                      <div style={{ fontSize: '14px', color: '#64748b', marginTop: 'auto' }}>
                        <p><strong>Atlas PHM</strong> • System Recommendation</p>
                        <p>Document ID: {deepLink ? deepLink.documentId.toUpperCase() : 'N/A'}</p>
                        <p>Generated: {new Date().toLocaleDateString('en-GB', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}</p>
                      </div>
                    </div>
                    <OptionPage key={option.optionId} data={option} />
                    <div className="dp-print-footer">
                      <p>
                        {IP_NOTICE_MINIMAL} |
                        Generated: {new Date().toLocaleDateString('en-GB')}
                        {deepLink ? ` | Document ID: ${deepLink.documentId.toUpperCase()}` : ''}
                      </p>
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {/* COMPLETE BUNDLE (LEGACY): Everything based on selections */}
          {exportBundle === 'all' && (
            <>
              {/* Page 0: Cover / Context */}
              {selections.includeCover && <CoverPage data={packWithDocRef.cover} />}

              {/* Page 1: Savings Overview (if year1 data available) */}
              {savingsPageProps && <SavingsPageWrapper {...savingsPageProps} />}

              {/* Page 2: Technical Details (if year1 data available) */}
              {howWeWorkedItOutProps && <HowWeWorkedItOutWrapper {...howWeWorkedItOutProps} />}

              {/* Page 3+: One A4 per selected option */}
              {visibleOptions.map((optData) => (
                <OptionPage key={optData.optionId} data={optData} />
              ))}

              {/* Final page: Summary & next steps */}
              {selections.includeSummary && visibleSummary.rows.length > 0 && (
                <SummaryPage data={visibleSummary} />
              )}

              {/* Portrait SWOT Stack page (if available) */}
              {packWithDocRef.swotPage && (
                <AsymmetricSwotPage data={packWithDocRef.swotPage} />
              )}

              {/* Technical Appendix with diagrams (if available) */}
              {packWithDocRef.technicalAppendix && (
                <TechnicalAppendixPage data={packWithDocRef.technicalAppendix} />
              )}

              {/* Technical Disclaimer page (if available) */}
              {packWithDocRef.technicalDisclaimer && (
                <TechnicalDisclaimerPage data={packWithDocRef.technicalDisclaimer} />
              )}

              {/* Appendices (optional, printed last) */}
              {visibleAppendices.map((appData, idx) => (
                <AppendixPage key={appData.id} data={appData} index={idx} />
              ))}

              {/* Footer: QR code + copyright + document ID (print only) */}
              <div className="dp-print-footer">
                {deepLink && (
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '12px',
                    paddingBottom: '12px',
                    borderBottom: '1px solid #e2e8f0',
                  }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '8px',
                      background: 'white',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                    }}>
                      <QRCode url={deepLink.url} size={100} />
                    </div>
                    <p style={{ fontSize: '10px', color: '#64748b', marginTop: '4px', marginBottom: '4px' }}>
                      Scan to view your interactive summary
                    </p>
                    <a 
                      href={deepLink.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '9px',
                        color: '#2563eb',
                        textDecoration: 'underline',
                        display: 'inline-block',
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: 10
                      }}
                    >
                      Open interactive report
                    </a>
                  </div>
                )}
                <p>
                  {IP_NOTICE_MINIMAL} |
                  Generated: {new Date().toLocaleDateString('en-GB')}
                  {deepLink ? ` | Document ID: ${deepLink.documentId.toUpperCase()}` : ''}
                  {' | Report layout: V2'}
                </p>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default ExportPage;
